// Updated JobSubmissionService.tsx
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import { Address } from '../../models/Address.interface';
import { ChatMessageProps } from './ChatMessage';
import { Attachment } from '../../models/Attachment.interface';
import { JobContractorMatch } from '../../models/JobContractorMatch.interface'

// Add this new interface at the top of JobSubmissionService.tsx
// This is for the initial save that only requires basic information
interface InitialJobParams {
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  contactPersonRole: string;
  propertyAddress: Address;
  currentUserId: string | null;
  jobStatus: string;
  recaptchaToken?: string; // Optional for reCAPTCHA validation
}

// Keep the existing SaveInitialJobParams for full job data
interface SaveInitialJobParams {
  jobTitle: string;
  jobDescription: string;
  jobSummary: string;
  jobDetail: string;
  jobCategories: string[];
  propertyAddress: Address;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  contactPersonRole: string;
  currentUserId: string | null;
  jobStatus: string;
  recaptchaToken?: string;
}

interface UpdateJobParams {
  jobTitle?: string;
  jobDescription?: string;
  jobSummary?: string;
  seoUrl?: string;
  jobDetail?: string;
  jobCategories?: string[];
  propertyAddress?: Address;
  jobAttachments?: Attachment[];
  complete?: boolean;
  chatMessages?: ChatMessageProps[];
  jobStatus?: string;
  ownerName?: string;
  ownerEmail?: string;
  ownerPhone?: string;
  contactPersonRole?: string;
  currentUserId?: string | null;
  dtSubmitted?: string;
  recaptchaToken?: string;
}

interface SaveJobResult {
  success: boolean;
  jobId?: string;
  matchedContractorCount?: number;
  error?: any;
}

/**
 * Helper function to save job data to MySQL
 * This runs asynchronously and doesn't block the Firebase save
 */
const saveJobToMySQL = async (endpoint: string, jobData: any): Promise<void> => {
  try {
    const serverUrl = process.env.REACT_APP_SERVER || 'https://www.bidbro.com/api';
    const apiUrl = `${serverUrl}/jobs/${endpoint}`;
    
    console.log(`📤 Saving to MySQL: ${apiUrl}`);
    
    const response = await fetch(apiUrl, {
      method: endpoint === 'initial' ? 'POST' : 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(jobData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`MySQL save failed: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log(`✅ Successfully saved to MySQL: ${result.message}`);
    
  } catch (error) {
    console.error(`❌ Error saving to MySQL (${endpoint}):`, error);
    // Don't throw the error - this is async backup, shouldn't fail the main operation
  }
};

/**
 * Finds contractors that match job criteria and creates job-contractor match records
 * Returns the number of matched contractors
 */
export const findMatchingContractors = async (jobId: string): Promise<number> => {
  const firestore = firebase.firestore();
  
  try {
    // Get job data
    const jobDoc = await firestore.collection('jobs').doc(jobId).get();
    if (!jobDoc.exists) {
      throw new Error(`Job ${jobId} not found`);
    }
    
    const jobData = jobDoc.data() as any;
    if (!jobData) {
      throw new Error(`No data found for job ${jobId}`);
    }
    
    jobData.id = jobId;
    const jobCategories = jobData.jobCategories || [];
    const propertyAddress = jobData.propertyAddress || {};
    
    // Find contractors that match job categories and location
    const contractorsSnapshot = await firestore.collection('contractors').get();
    const matchedContractors: any[] = [];
    
    contractorsSnapshot.forEach((doc) => {
      const contractor = { id: doc.id, ...doc.data() } as any;
      
      // Check if any of the contractor's categories match the job categories
      const contractorCategories = contractor.categories || [];
      const hasMatchingCategory = contractorCategories.some((category: string) => 
        jobCategories.includes(category)
      );
      
      if (hasMatchingCategory) {
        // Check if any of the contractor's search locations match the job location
        const contractorSearches = contractor.searches || [];
        const locationMatch = contractorSearches.some(
          (search: any) => 
            search.city === propertyAddress.city &&
            search.state === propertyAddress.state
        );
        
        if (locationMatch) {
          matchedContractors.push(contractor);
        }
      }
    });
    
    // Create job-contractor match records
    const batch = firestore.batch();
    
    matchedContractors.forEach((contractor) => {
      const matchRef = firestore.collection('jobContractorMatches').doc();
      const matchData = {
        jobId,
        contractorId: contractor.id,
        matchedAt: firebase.firestore.Timestamp.now(),
        status: 'matched',
        hasViewed: false,
        hasBid: false,
        isSelected: false,
        isInterested: false
      };
      batch.set(matchRef, matchData);
    });
    
    // Commit the batch
    if (matchedContractors.length > 0) {
      await batch.commit();
    }
    
    console.log(`Found ${matchedContractors.length} matching contractors for job ${jobId}`);
    return matchedContractors.length;
    
  } catch (error) {
    console.error('Error finding matching contractors:', error);
    throw error;
  }
};

/**
 * Sends initial job notification to customer
 */
const sendCustomerInitialJobNotification = async (jobData: any): Promise<void> => {
  try {
    const serverUrl = process.env.REACT_APP_SERVER || 'https://www.bidbro.com/api';
    const apiUrl = `${serverUrl}/customer-initial-job-notification`;
    
    await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobData }),
    });
    
    console.log('✅ Initial job notification sent to customer');
  } catch (error) {
    console.error('❌ Error sending initial job notification:', error);
    // Don't throw - notification failure shouldn't fail job creation
  }
};

/**
 * Sends job submission notification to customer
 */
const sendCustomerJobSubmittedNotification = async (jobData: any, matchedContractorCount: number): Promise<void> => {
  try {
    const serverUrl = process.env.REACT_APP_SERVER || 'https://www.bidbro.com/api';
    const apiUrl = `${serverUrl}/customer-job-submitted-notification`;
    
    await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobData, matchedContractorCount }),
    });
    
    console.log('✅ Job submission notification sent to customer');
  } catch (error) {
    console.error('❌ Error sending job submission notification:', error);
    // Don't throw - notification failure shouldn't fail job submission
  }
};

/**
 * Saves initial job data to Firebase when user moves from Step 1 to Step 2
 * Creates a job in Draft status with minimal required information
 * Also saves to MySQL asynchronously
 */
export const saveInitialJobToFirebase = async (params: InitialJobParams): Promise<SaveJobResult> => {
  const firestore = firebase.firestore();

  try {
    const createdDate = new Date().toISOString();

    // Prepare minimal job data for initial save
    const initialJobData = {
      // Required fields for initial save
      ownerName: params.ownerName,
      ownerEmail: params.ownerEmail,
      ownerPhone: params.ownerPhone,
      contactPersonRole: params.contactPersonRole,
      propertyAddress: params.propertyAddress,
      jobStatus: params.jobStatus, // 'Draft'
      dtCreated: createdDate,
      userId: params.currentUserId, // Can be null for non-authenticated users
      
      // Default/empty values for fields that will be filled later
      jobTitle: '',
      jobDescription: '',
      jobSummary: '',
      seoUrl: '',
      jobDetail: '',
      jobCategories: [],
      jobAttachments: [],
      complete: false,
      chatMessages: [],
      
      // Tracking fields
      isInitialSave: true,
      dtLastModified: createdDate,
      
      // Initialize matching status
      matchingStatus: 'pending', // 'pending', 'matching', 'completed'
      matchedContractorCount: 0
    };

    // Handle reCAPTCHA validation if token is provided
    if (params.recaptchaToken) {
      // Add your reCAPTCHA validation logic here
      console.log('Validating reCAPTCHA token:', params.recaptchaToken);
      // You can call your backend API to verify the token
    }

    // Save initial job data to Firestore
    const docRef = await firestore.collection('jobs').add(initialJobData);
    const jobId = docRef.id;
    
    console.log('Initial job saved to Firebase with ID:', jobId);
    
    // Send initial job created notification to customer
    const jobDataForNotification = {
      ...initialJobData,
      id: jobId
    };
    
    await sendCustomerInitialJobNotification(jobDataForNotification);
    
    // Asynchronously save to MySQL (don't await to avoid blocking)
    const mysqlJobData = {
      jobId,
      ownerName: params.ownerName,
      ownerEmail: params.ownerEmail,
      ownerPhone: params.ownerPhone,
      contactPersonRole: params.contactPersonRole,
      propertyAddress: params.propertyAddress,
      userId: params.currentUserId,
      jobStatus: params.jobStatus,
      dtCreated: createdDate,
      isInitialSave: true,
      matchingStatus: 'pending'
    };
    
    // Fire and forget - runs in background
    saveJobToMySQL('initial', mysqlJobData).catch(error => {
      console.warn('MySQL initial save failed but continuing with Firebase operation:', error);
    });
    
    return { 
      success: true, 
      jobId
    };
  } catch (error) {
    console.error('Error saving initial job to Firebase:', error);
    return { success: false, error };
  }
};

/**
 * Updates an existing job in Firebase
 * Can be used for incremental updates or final submission
 * Also updates MySQL asynchronously
 */
export const updateJobInFirebase = async (
  jobId: string, 
  updateData: UpdateJobParams, 
  isFinalSubmission: boolean = false
): Promise<SaveJobResult> => {
  const firestore = firebase.firestore();

  try {
    const jobRef = firestore.collection('jobs').doc(jobId);
    
    // Prepare update data
    const updates: any = {
      ...updateData,
      dtLastModified: new Date().toISOString()
    };

    // Handle file uploads if there are new attachments
    if (updateData.jobAttachments) {
      // Note: File uploads should be handled separately before calling this function
      // This assumes jobAttachments contains already uploaded file URLs
      updates.jobAttachments = updateData.jobAttachments;
    }

    // If this is a final submission, handle contractor matching and notifications
    if (isFinalSubmission) {
      updates.dtSubmitted = new Date().toISOString();
      updates.jobStatus = 'Submitted';
      updates.complete = true;
      updates.isInitialSave = false;
      updates.matchingStatus = 'matching'; // Set to matching status

      // Update the job first - this allows user to return to page immediately
      await jobRef.update(updates);
      
      // Start asynchronous matching and notification process
      // This runs in the background without blocking the user
      performAsyncContractorMatching(jobId);
      
      // Asynchronously save to MySQL (don't await to avoid blocking)
      const mysqlUpdateData = {
        ...updateData,
        isFinalSubmission: true,
        dtSubmitted: updates.dtSubmitted,
        matchingStatus: 'matching'
      };
      
      // Fire and forget - runs in background
      saveJobToMySQL(`${jobId}`, mysqlUpdateData).catch(error => {
        console.warn('MySQL final submission update failed but continuing:', error);
      });
      
      return { 
        success: true, 
        jobId 
      };
    } else {
      // Regular update (not final submission)
      await jobRef.update(updates);
      
      // Asynchronously save to MySQL (don't await to avoid blocking)
      saveJobToMySQL(`${jobId}`, updateData).catch(error => {
        console.warn('MySQL update failed but continuing with Firebase operation:', error);
      });
      
      return { 
        success: true, 
        jobId 
      };
    }
  } catch (error) {
    console.error('Error updating job in Firebase:', error);
    return { success: false, error };
  }
};

/**
 * Handles the asynchronous contractor matching and notification process
 * This runs in the background after final job submission
 */
const performAsyncContractorMatching = async (jobId: string): Promise<void> => {
  try {
    console.log('🔄 Starting async contractor matching for job:', jobId);
    
    // Find matching contractors
    const matchedContractorCount = await findMatchingContractors(jobId);
    
    // Update job with matching results
    const firestore = firebase.firestore();
    await firestore.collection('jobs').doc(jobId).update({
      matchedContractorCount,
      matchingStatus: 'completed'
    });
    
    // Get the updated job data for notifications
    const updatedJobDoc = await firestore.collection('jobs').doc(jobId).get();
    const updatedJobData = { id: jobId, ...updatedJobDoc.data() };
    
    // Send notifications
    await sendCustomerJobSubmittedNotification(updatedJobData, matchedContractorCount);
    
    // Update MySQL with final matching results
    const mysqlUpdateData = {
      matchingStatus: 'completed',
      matchedContractorCount
    };
    
    await saveJobToMySQL(`${jobId}`, mysqlUpdateData);
    
    console.log('✅ Async contractor matching completed for job:', jobId);
    
  } catch (error) {
    console.error('❌ Error in async contractor matching:', error);
    
    // Update job status to indicate matching failed
    try {
      const firestore = firebase.firestore();
      await firestore.collection('jobs').doc(jobId).update({
        matchingStatus: 'pending',
        matchingError: error instanceof Error ? error.message : 'Unknown error during matching'
      });
    } catch (updateError) {
      console.error('❌ Failed to update job with matching error:', updateError);
    }
  }
};

/**
 * Listens to job matching status updates in real-time
 * Returns an unsubscribe function
 */
export const subscribeToJobMatchingStatus = (
  jobId: string, 
  callback: (matchingStatus: string, matchedContractorCount?: number) => void
): (() => void) => {
  const firestore = firebase.firestore();
  
  return firestore.collection('jobs').doc(jobId).onSnapshot((doc) => {
    if (doc.exists) {
      const data = doc.data();
      if (data) {
        callback(data.matchingStatus || 'pending', data.matchedContractorCount);
      }
    }
  }, (error) => {
    console.error('Error listening to job matching status:', error);
  });
};

/**
 * Uploads job attachments to Firebase Storage and returns attachment metadata
 */
export const uploadJobAttachmentsToFirebase = async (files: File[], jobId?: string): Promise<Attachment[]> => {
  const storage = firebase.storage();
  
  try {
    const uploadPromises = files.map(async (file: File, index: number) => {
      const path = jobId ? `jobs/${jobId}/${Date.now()}_${file.name}` : `jobs/${Date.now()}_${file.name}`;
      const fileRef = storage.ref(path);
      await fileRef.put(file);
      const url = await fileRef.getDownloadURL();

      return {
        id: `attachment-${Date.now()}-${index}`,
        url,
        caption: '',
        order: index
      };
    });

    const attachments = await Promise.all(uploadPromises);
    console.log('✅ Job attachments uploaded successfully:', attachments.length);
    
    return attachments;
  } catch (error) {
    console.error('❌ Error uploading job attachments:', error);
    throw error;
  }
};

/**
 * Fetches job-contractor matches for a job
 */
export const fetchJobContractorMatches = async (jobId: string): Promise<JobContractorMatch[]> => {
  const firestore = firebase.firestore();
  
  try {
    const matchesSnapshot = await firestore
      .collection('jobContractorMatches')
      .where('jobId', '==', jobId)
      .get();
      
    return matchesSnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    })) as JobContractorMatch[];
  } catch (error) {
    console.error('Error fetching job-contractor matches:', error);
    throw error;
  }
};

/**
 * Fetches contractor details for job-contractor matches
 */
export const fetchMatchedContractors = async (jobId: string): Promise<any[]> => {
  const firestore = firebase.firestore();
  
  try {
    const matches = await fetchJobContractorMatches(jobId);
    
    const contractorPromises = matches.map(async (match) => {
      const contractorId = match.contractorId;
      
      const contractorSnapshot = await firestore
        .collection('contractors')
        .where('contractorId', '==', contractorId)
        .limit(1)
        .get();
        
      if (contractorSnapshot.empty) {
        console.log("No contractor found with contractorId:", contractorId);
        return null;
      }
      
      const contractorDoc = contractorSnapshot.docs[0];
      const contractorData = contractorDoc.data();
      
      return {
        id: contractorDoc.id,
        ...contractorData,
        matchInfo: match
      };
    });
    
    const contractors = await Promise.all(contractorPromises);
    return contractors.filter(Boolean);
  } catch (error) {
    console.error('Error fetching matched contractors:', error);
    throw error;
  }
};

/**
 * Updates contractor selection status
 */
export const updateContractorSelection = async (jobId: string, contractorId: string, isSelected: boolean): Promise<void> => {
  try {
    const firestore = firebase.firestore();
    
    await firestore
      .collection('contractors')
      .doc(contractorId)
      .collection('matched-jobs')
      .doc(jobId)
      .update({ isSelected });
      
    console.log(`Updated selection status for contractor ${contractorId} on job ${jobId} to ${isSelected}`);
  } catch (error) {
    console.error('Error updating contractor selection:', error);
    throw error;
  }
};

/**
 * Notifies selected contractors about a job opportunity
 */
export const notifyContractors = async (jobId: string, contractorIds: string[] = []): Promise<void> => {
  try {
    const serverUrl = process.env.REACT_APP_SERVER || 'https://www.bidbro.com/api';
    const apiUrl = `${serverUrl}/notify-contractors`;
  
    fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobId, contractorIds }),
    })
    .then(response => response.json())
    .then(data => {
      console.log('Contractors notified:', data);
    })
    .catch((error: any) => {
      console.error('Error notifying contractors:', error);
    });
  } catch (error) {
    console.error('Error in notifyContractors:', error);
  }
};

/**
 * Deletes job attachments from storage
 */
export const deleteJobAttachments = async (urls: string[]): Promise<void> => {
  const storage = firebase.storage();
  
  try {
    await Promise.all(
      urls.map(async (url) => {
        try {
          const fileRef = storage.refFromURL(url);
          await fileRef.delete();
        } catch (error) {
          console.error('Error deleting file from storage:', error);
        }
      })
    );
  } catch (error) {
    console.error('Error in deleteJobAttachments:', error);
    throw error;
  }
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use saveInitialJobToFirebase and updateJobInFirebase instead
 */
export const saveJobToFirebase = async (params: any): Promise<SaveJobResult> => {
  console.warn('saveJobToFirebase is deprecated. Use saveInitialJobToFirebase and updateJobInFirebase instead.');
  
  const firestore = firebase.firestore();
  const storage = firebase.storage();

  try {
    // Upload files and create attachments array with captions and URLs
    const uploadPromises = params.jobAttachments.map(async (file: File) => {
      const fileRef = storage.ref(`jobs/${Date.now()}_${file.name}`);
      await fileRef.put(file);
      const url = await fileRef.getDownloadURL();

      return {
        id: `attachment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        url,
        caption: '',
        order: 0
      };
    });

    const newAttachments = await Promise.all(uploadPromises);

    // Combine existing and new attachments
    const updatedAttachments: Attachment[] = [
      ...params.existingAttachments,
      ...newAttachments
    ].map((attachment, index) => ({
      id: attachment.id || `attachment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      url: attachment.url,
      caption: attachment.caption || '',
      order: index
    }));

    const createdDate = new Date().toISOString();

    // Prepare job data object
    const jobData = {
      jobTitle: params.jobTitle,
      jobDescription: params.jobDescription,
      jobSummary: params.jobSummary,
      seoUrl: params.seoUrl,
      jobDetail: params.jobDetail,
      jobCategories: params.jobCategories,
      propertyAddress: params.propertyAddress,
      jobAttachments: updatedAttachments,
      complete: true,
      chatMessages: params.chatMessages,
      jobStatus: params.jobStatus,
      dtCreated: createdDate,
      ownerName: params.ownerName,
      ownerEmail: params.ownerEmail,
      ownerPhone: params.ownerPhone,
      contactPersonRole: params.contactPersonRole,
      userId: params.currentUserId
    };

    // Save job data to Firestore
    const docRef = await firestore.collection('jobs').add(jobData);
    const jobId = docRef.id;
    console.log('Job saved to Firebase successfully with ID:', jobId);
    
    // Find matching contractors
    const matchedContractorCount = await findMatchingContractors(jobId);
    
    // Get the updated job data for notifications
    const updatedJobDoc = await docRef.get();
    const updatedJobData = { id: jobId, ...updatedJobDoc.data() };
    
    // Send notifications
    const jobDataForCustomer = {
      ...updatedJobData,
      id: jobId
    };
    
    sendCustomerJobSubmittedNotification(jobDataForCustomer, matchedContractorCount);
    
    return { 
      success: true, 
      jobId,
      matchedContractorCount 
    };
  } catch (error) {
    console.error('Error saving job to Firebase:', error);
    return { success: false, error };
  }
};